import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Check, AlertCircle } from "lucide-react";
import { GOAL_LABELS, GOAL_PROMPTS } from "@/data/onboarding-topics";
import type { LearningGoal, HebrewLevel } from "@/types";
import type { OnboardingWord } from "@/hooks/useFirestore";
import { cn } from "@/lib/utils";

const FUN_FACTS = [
  "Hebrew is one of the only languages ever successfully revived from extinction",
  "Hebrew reads right-to-left, but numbers are read left-to-right",
  "The Hebrew alphabet has 22 letters — all consonants. Vowels are optional dots and dashes",
  "Modern Hebrew was revived by one man — Eliezer Ben-Yehuda — in the late 1800s",
  "The word 'alphabet' comes from the first two Hebrew letters: Aleph and Bet",
  "In Hebrew, the word for 'face' (פנים) is always plural",
  "Hebrew verbs follow 7 patterns called binyanim — learn the pattern, predict the word",
  "The Hebrew word for 'computer' (מחשב) literally means 'thinker'",
  "There are about 9 million Hebrew speakers worldwide today",
  "The Hebrew word for 'word' (מילה) also means 'circumcision'",
  "Every Hebrew noun is either masculine or feminine — even tables and chairs",
  "Ben-Yehuda's son was the first native Modern Hebrew speaker in over 1,500 years",
  "The Hebrew word for 'electricity' (חשמל) comes from the Book of Ezekiel",
  "Hebrew has a special verb form just for commands — the imperative (ציווי)",
  "The root system means most Hebrew words come from just 3 consonants",
  "The Hebrew word for 'orange' (תפוז) is short for 'golden apple' (תפוח זהב)",
  "Hebrew slang borrows heavily from Arabic, English, Yiddish, and Russian",
  "The Dead Sea Scrolls contain Hebrew texts over 2,000 years old",
  "Hebrew doesn't have uppercase or lowercase letters",
  "Five Hebrew letters change shape when they appear at the end of a word",
  "The Hebrew calendar is lunisolar — months follow the moon, years follow the sun",
  "The word 'amen' (אמן) comes from Hebrew, meaning 'so be it'",
  "Israeli street signs are written in Hebrew, Arabic, and English",
  "The Hebrew word for 'vaccine' (חיסון) comes from the root for 'immune' or 'strong'",
  "In Hebrew, you 'open' (פותח) a light, not 'turn it on'",
  "The Hebrew word for 'balcony' (מרפסת) appears once in the Bible",
];

type StepState = "editing" | "generating" | "done" | "error";

const GOALS: LearningGoal[] = ["aliyah", "heritage", "religious", "travel", "military", "general"];
const MAX_CHARS = 300;

interface PersonalPromptStepProps {
  level: HebrewLevel;
  selectedGoal: LearningGoal | undefined;
  prompt: string;
  onSelectGoal: (goal: LearningGoal) => void;
  onChangePrompt: (prompt: string) => void;
  onGenerate: () => void;
  onBack: () => void;
  state: StepState;
  progressMessage?: string;
  progressPercent?: number;
  generatedWords: OnboardingWord[];
  error: string | null;
  onRetry: () => void;
  onSkip: () => void;
  onFinish: () => void;
}

export function PersonalPromptStep({
  selectedGoal,
  prompt,
  onSelectGoal,
  onChangePrompt,
  onGenerate,
  onBack,
  state,
  progressMessage,
  progressPercent = 0,
  generatedWords,
  error,
  onRetry,
  onSkip,
  onFinish,
}: PersonalPromptStepProps) {
  const [goalJustSelected, setGoalJustSelected] = useState(false);

  const handleGoalClick = (goal: LearningGoal) => {
    onSelectGoal(goal);
    onChangePrompt(GOAL_PROMPTS[goal]);
    setGoalJustSelected(true);
  };

  const handlePromptChange = (text: string) => {
    if (text.length <= MAX_CHARS) {
      onChangePrompt(text);
      setGoalJustSelected(false);
    }
  };

  // Rotating fun facts
  const [factIndex, setFactIndex] = useState(() => Math.floor(Math.random() * FUN_FACTS.length));
  useEffect(() => {
    if (state !== "generating") return;
    const interval = setInterval(() => {
      setFactIndex((prev) => (prev + 1) % FUN_FACTS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [state]);

  // Trickle progress during long API call so bar always moves
  const [displayPercent, setDisplayPercent] = useState(0);
  const trickleRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (state === "generating") {
      setDisplayPercent(progressPercent);

      // If real progress is low (waiting on API), trickle up slowly
      if (progressPercent < 30) {
        trickleRef.current = setInterval(() => {
          setDisplayPercent((prev) => {
            // Slowly approach 28% but never reach it — leaves room for real progress to take over
            if (prev >= 28) return prev;
            return prev + (28 - prev) * 0.08;
          });
        }, 500);
      } else {
        // Real progress is happening, snap to it
        if (trickleRef.current) clearInterval(trickleRef.current);
        setDisplayPercent(progressPercent);
      }

      return () => {
        if (trickleRef.current) clearInterval(trickleRef.current);
      };
    } else {
      setDisplayPercent(0);
    }
  }, [state, progressPercent]);

  if (state === "generating") {
    const shown = Math.round(Math.max(displayPercent, progressPercent));
    return (
      <div className="flex flex-col items-center text-center gap-6 py-8">
        <div className="w-full max-w-xs">
          <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-blue-500"
              animate={{ width: `${shown}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
          </div>
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 text-right">
            {shown}%
          </p>
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Setting up Shoresh for you
          </h2>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {progressMessage || "Curating words just for you"}
          </p>
          <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
            This is a one-time setup — it'll only take a moment!
          </p>
        </div>

        <div className="w-full max-w-xs h-12 relative">
          <AnimatePresence mode="wait">
            <motion.p
              key={factIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
              className="text-xs text-gray-400 dark:text-gray-500 italic absolute inset-0 flex items-center justify-center text-center"
            >
              {FUN_FACTS[factIndex]}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="flex flex-col items-center text-center gap-6 py-8">
        <AlertCircle className="w-12 h-12 text-red-500" />
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Something went wrong
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {error || "Failed to generate vocabulary. Please try again."}
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onSkip}>
            Skip for now
          </Button>
          <Button onClick={onRetry}>Try Again</Button>
        </div>
      </div>
    );
  }

  if (state === "done") {
    return (
      <div className="flex flex-col items-center text-center gap-6">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
        >
          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
            <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
        </motion.div>

        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Your vocabulary is ready!
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {generatedWords.length} words added to your word bank
          </p>
        </div>

        <div className="w-full max-w-sm max-h-60 overflow-y-auto space-y-1">
          {generatedWords.map((word, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.08 }}
              className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm"
            >
              <span className="font-medium text-right" dir="rtl">
                {word.hebrew}
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                {word.english}
              </span>
            </motion.div>
          ))}
        </div>

        <Button onClick={onFinish} className="w-full max-w-sm" size="lg">
          Start Learning
        </Button>
      </div>
    );
  }

  // Editing state
  return (
    <div className="flex flex-col items-center text-center gap-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Tell us about yourself
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Pick a goal or write your own — we'll curate vocabulary just for you
        </p>
      </div>

      {/* Goal chips */}
      <div className="w-full max-w-sm flex flex-wrap justify-center gap-2">
        {GOALS.map((goal) => {
          const info = GOAL_LABELS[goal];
          const isSelected = selectedGoal === goal && goalJustSelected;
          return (
            <button
              key={goal}
              onClick={() => handleGoalClick(goal)}
              className={cn(
                "px-3 py-1.5 rounded-full border text-xs font-medium transition-colors",
                isSelected
                  ? "border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-400"
                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:border-gray-600"
              )}
            >
              {info.emoji} {info.label}
            </button>
          );
        })}
      </div>

      {/* Free-text prompt */}
      <div className="w-full max-w-sm">
        <textarea
          value={prompt}
          onChange={(e) => handlePromptChange(e.target.value)}
          placeholder="Example: I'm moving to Tel Aviv in 3 months. I work in tech and need to talk to my landlord, order food, and navigate the bus system."
          className="w-full h-28 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <div className="flex justify-end mt-1">
          <span className={cn(
            "text-xs",
            prompt.length > MAX_CHARS * 0.9
              ? "text-amber-500"
              : "text-gray-400 dark:text-gray-500"
          )}>
            {prompt.length}/{MAX_CHARS}
          </span>
        </div>
      </div>

      <div className="flex gap-3 w-full max-w-sm">
        <Button variant="outline" onClick={onBack} size="lg">
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <Button
          onClick={onGenerate}
          className="flex-1"
          size="lg"
          disabled={prompt.trim().length < 10}
        >
          Build My Vocabulary
        </Button>
      </div>
    </div>
  );
}
